import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAllQuotes, deleteQuote } from "@/services/quoteService";
import type { Quote, FirestoreDate } from "@/types/quote";
import {
  Search,
  Pencil,
  Trash2,
  Eye,
  Plus,
  ChevronLeft,
  ChevronRight,
  FileText,
  DollarSign,
  TrendingUp,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

const PAGE_SIZES = [10, 20, 50, 100];

/** Convierte una fecha de Firestore a Date nativo. */
function toDate(val: FirestoreDate | undefined | null): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof (val as { toDate?: () => Date }).toDate === "function") {
    return (val as { toDate(): Date }).toDate();
  }
  if ("seconds" in val) {
    return new Date(val.seconds * 1000);
  }
  return null;
}

/** Formatea una fecha de Firestore a string legible. */
function formatDate(val: FirestoreDate | undefined | null): string {
  const d = toDate(val);
  if (!d) return "--";
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Formatea un monto a moneda. */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-DO", {
    style: "currency",
    currency: "DOP",
    minimumFractionDigits: 0,
  }).format(amount);
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: "Borrador",
  SENT: "Enviada",
  VIEWED: "Vista",
  EXPIRED: "Expirada",
};

function statusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "SENT":
      return "default";
    case "VIEWED":
      return "secondary";
    case "EXPIRED":
      return "destructive";
    default:
      return "outline";
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "DRAFT":
      return "bg-gray-100 text-gray-700 border-gray-300";
    case "SENT":
      return "bg-blue-100 text-blue-700 border-blue-300";
    case "VIEWED":
      return "bg-green-100 text-green-700 border-green-300";
    case "EXPIRED":
      return "bg-red-100 text-red-700 border-red-300";
    default:
      return "";
  }
}

type StatusTab = "all" | "DRAFT" | "SENT" | "VIEWED" | "EXPIRED";

export function Quotes() {
  const queryClient = useQueryClient();
  const { data: rawQuotes = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.quotes,
    queryFn: getAllQuotes,
  });

  const quotes = rawQuotes as Quote[];

  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  // Stats
  const totalQuotes = quotes.length;
  const totalAmount = useMemo(() => quotes.reduce((sum, q) => sum + (q.totalAmount ?? 0), 0), [quotes]);
  const sentCount = useMemo(() => quotes.filter((q) => q.status === "SENT" || q.status === "VIEWED").length, [quotes]);
  const convertedCount = useMemo(() => quotes.filter((q) => q.convertedToSale).length, [quotes]);
  const conversionRate = sentCount > 0 ? Math.round((convertedCount / sentCount) * 100) : 0;
  const pendingCount = useMemo(() => quotes.filter((q) => q.status === "DRAFT").length, [quotes]);

  // Filter
  const filtered = useMemo(() => {
    let list = [...quotes];
    if (statusTab !== "all") {
      list = list.filter((q) => q.status === statusTab);
    }
    if (search.trim()) {
      const s = search.toLowerCase().trim();
      list = list.filter(
        (q) =>
          q.customer?.name?.toLowerCase().includes(s) ||
          q.quoteNumber?.toLowerCase().includes(s) ||
          q.customer?.email?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [quotes, statusTab, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar esta cotización? Esta acción no se puede deshacer.")) return;
    try {
      await deleteQuote(id);
      toast.success("Cotización eliminada");
      await queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
    } catch {
      toast.error("Error al eliminar la cotización");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Cotizaciones</h1>
          <p className="text-muted-foreground">Gestión de cotizaciones y presupuestos</p>
        </div>
        <Button asChild>
          <Link to="/quotes/new" className="gap-2">
            <Plus className="h-4 w-4" />
            Nueva Cotización
          </Link>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Total Cotizaciones</p>
            </div>
            <p className="text-2xl font-semibold text-primary">{totalQuotes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Monto Total</p>
            </div>
            <p className="text-2xl font-semibold">{formatCurrency(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Tasa de Conversión</p>
            </div>
            <p className="text-2xl font-semibold">{conversionRate}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Pendientes</p>
            </div>
            <p className="text-2xl font-semibold">{pendingCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Status tabs */}
      <Tabs
        value={statusTab}
        onValueChange={(v) => {
          setStatusTab(v as StatusTab);
          setPage(1);
        }}
      >
        <TabsList>
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="DRAFT">Borrador</TabsTrigger>
          <TabsTrigger value="SENT">Enviadas</TabsTrigger>
          <TabsTrigger value="VIEWED">Vistas</TabsTrigger>
          <TabsTrigger value="EXPIRED">Expiradas</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Table */}
      <Card>
        <CardHeader className="space-y-4 pb-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente, # cotización, email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {total > 0
                ? `Mostrando ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, total)} de ${total}`
                : "Sin resultados"}
            </p>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm tabular-nums">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Cargando...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#Cotización</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Monto Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="w-[120px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="font-medium">{q.quoteNumber ?? "--"}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{q.customer?.name ?? "--"}</p>
                        <p className="text-xs text-muted-foreground">{q.customer?.email ?? ""}</p>
                      </div>
                    </TableCell>
                    <TableCell>{q.items?.length ?? 0}</TableCell>
                    <TableCell>{formatCurrency(q.totalAmount ?? 0)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(q.status)} className={statusBadgeClass(q.status)}>
                        {STATUS_LABELS[q.status] ?? q.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(q.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/quotes/${q.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/quotes/edit/${q.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No hay cotizaciones</p>
              <Button asChild className="mt-4">
                <Link to="/quotes/new">Crear primera cotización</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
